import type { GeofenceStatus, GeolocationInput, SiteGeofenceConfig } from "../schemas/geofence.js";

const EARTH_RADIUS_METERS = 6_371_000;

/** Haversine distance between two WGS-84 coordinates in meters. */
export function calculateDistanceMeters(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

export type GeofenceSiteInput = Pick<
  SiteGeofenceConfig,
  "enabled" | "strictMode" | "latitude" | "longitude" | "radiusMeters"
>;

export type GeofenceValidationResult = {
  status: GeofenceStatus;
  distanceFromSiteMeters: number | null;
  blocked: boolean;
  requiresOverrideReason: boolean;
};

export function validateGeofence(
  site: GeofenceSiteInput,
  location: GeolocationInput | null | undefined,
  overrideReason?: string | null,
): GeofenceValidationResult {
  if (!site.enabled) {
    return {
      status: "MANUAL_ENTRY",
      distanceFromSiteMeters: null,
      blocked: false,
      requiresOverrideReason: false,
    };
  }

  if (!location) {
    if (overrideReason?.trim()) {
      return {
        status: "MANAGER_OVERRIDE",
        distanceFromSiteMeters: null,
        blocked: false,
        requiresOverrideReason: false,
      };
    }
    return {
      status: "GPS_UNAVAILABLE",
      distanceFromSiteMeters: null,
      blocked: site.strictMode,
      requiresOverrideReason: !site.strictMode,
    };
  }

  if (site.latitude == null || site.longitude == null || site.radiusMeters == null) {
    return {
      status: "GPS_UNAVAILABLE",
      distanceFromSiteMeters: null,
      blocked: false,
      requiresOverrideReason: false,
    };
  }

  const distance = calculateDistanceMeters(
    location.latitude,
    location.longitude,
    site.latitude,
    site.longitude,
  );

  if (distance <= site.radiusMeters) {
    return {
      status: "VERIFIED_ON_SITE",
      distanceFromSiteMeters: distance,
      blocked: false,
      requiresOverrideReason: false,
    };
  }

  if (overrideReason?.trim()) {
    return {
      status: "MANAGER_OVERRIDE",
      distanceFromSiteMeters: distance,
      blocked: false,
      requiresOverrideReason: false,
    };
  }

  return {
    status: "OUTSIDE_GEOFENCE",
    distanceFromSiteMeters: distance,
    blocked: site.strictMode,
    requiresOverrideReason: !site.strictMode,
  };
}

export function geofenceStatusLabel(status: GeofenceStatus): string {
  switch (status) {
    case "VERIFIED_ON_SITE":
      return "On site";
    case "OUTSIDE_GEOFENCE":
      return "Outside geofence";
    case "GPS_UNAVAILABLE":
      return "GPS unavailable";
    case "MANUAL_ENTRY":
      return "Manual entry";
    case "MANAGER_OVERRIDE":
      return "Manager override";
    default:
      return status;
  }
}

export const GEOFENCE_PRIVACY_NOTICE =
  "Location is captured only when you submit time or clock in/out — never tracked in the background.";
